import React, { useState, useMemo, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCoffee,
  faMugHot,
  faMugSaucer,
  faGlassWater,
  faCake,
  faIceCream,
  faSeedling,
  faClock,
  faPhone,
  faLocationDot,
  faEnvelope,
  faHeart,
  faStar,
  faCrown,
  faAward,
  faLeaf,
  faChevronDown,
  faChevronUp,
  faCircle,
} from "@fortawesome/free-solid-svg-icons";
import useSWR from "swr";

const fetcher = async (url) => {
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      Accept: "application/vnd.api+json",
    },
  });
  if (!response.ok)
    throw new Error(`API request failed with status ${response.status}`);
  return response.json();
};

export default function CoffeeShopMenu() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [hoveredItem, setHoveredItem] = useState(null);

  // Handle scroll for sticky nav
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch menu items
  const {
    data: menuData,
    error: menuError,
    isLoading: menuLoading,
  } = useSWR(
    `${process.env.REACT_APP_BASE_URL}/jsonapi/node/menu_item?sort=-created&include=field_category,field_image`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: 30000,
    },
  );

  // Fetch categories
  const { data: categoriesData } = useSWR(
    `${process.env.REACT_APP_BASE_URL}/jsonapi/taxonomy_term/menu_category?sort=name`,
    fetcher,
    {
      revalidateOnFocus: true,
    },
  );

  // Process menu items
  const menuItems = useMemo(() => {
    if (!menuData || !menuData.data) return [];

    const categoryLookup = {};
    if (categoriesData?.data) {
      categoriesData.data.forEach((cat) => {
        categoryLookup[cat.id] = cat.attributes.name;
      });
    }

    const imageLookup = {};
    if (menuData.included) {
      menuData.included.forEach((include) => {
        if (include.type === "file--file") {
          const imageUrl = include.attributes.uri?.url;
          if (imageUrl) {
            const fullUrl = imageUrl.startsWith("http")
              ? imageUrl
              : `${process.env.REACT_APP_BASE_URL}${imageUrl}`;
            imageLookup[include.id] = fullUrl;
          }
        }
      });
    }

    return menuData.data.map((item) => {
      const attributes = item.attributes;

      let category = "Uncategorized";
      let categoryId = null;
      if (item.relationships?.field_category?.data) {
        categoryId = item.relationships.field_category.data.id;
        category = categoryLookup[categoryId] || "Uncategorized";
      }

      let imageUrl = null;
      let imageId = null;
      if (item.relationships?.field_image?.data) {
        imageId = item.relationships.field_image.data.id;
        imageUrl = imageLookup[imageId] || null;
      }

      return {
        id: item.id,
        title: attributes.title,
        price: parseFloat(attributes.field_price) || 0,
        description: attributes.field_description || "",
        availability: attributes.field_availability,
        category: category,
        categoryId: categoryId,
        imageUrl: imageUrl,
        imageId: imageId,
      };
    });
  }, [menuData, categoriesData]);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set();
    menuItems.forEach((item) => {
      if (item.category !== "Uncategorized") {
        cats.add(item.category);
      }
    });
    return ["All", ...Array.from(cats)];
  }, [menuItems]);

  // Filter items by category
  const filteredItems = useMemo(() => {
    if (selectedCategory === "All") return menuItems;
    return menuItems.filter((item) => item.category === selectedCategory);
  }, [menuItems, selectedCategory]);

  // Group items by category
  const groupedItems = useMemo(() => {
    const groups = {};
    if (selectedCategory === "All") {
      filteredItems.forEach((item) => {
        if (!groups[item.category]) {
          groups[item.category] = [];
        }
        groups[item.category].push(item);
      });
    } else {
      groups[selectedCategory] = filteredItems;
    }
    return groups;
  }, [filteredItems, selectedCategory]);

  // Get category style
  const getCategoryStyle = (category) => {
    const styles = {
      Coffee: {
        icon: faCoffee,
        color: "#6F4E37",
        gradient: "linear-gradient(135deg, #6F4E37, #8B6914)",
      },
      "Non Coffee": {
        icon: faMugSaucer,
        color: "#8B7355",
        gradient: "linear-gradient(135deg, #8B7355, #A89070)",
      },
      Milkshakes: {
        icon: faGlassWater,
        color: "#F4A460",
        gradient: "linear-gradient(135deg, #F4A460, #FFB6C1)",
      },
      "Fresh Brewed": {
        icon: faMugHot,
        color: "#8B4513",
        gradient: "linear-gradient(135deg, #8B4513, #A0522D)",
      },
      Pastries: {
        icon: faCake,
        color: "#D2691E",
        gradient: "linear-gradient(135deg, #D2691E, #F4A460)",
      },
      Desserts: {
        icon: faIceCream,
        color: "#D4A574",
        gradient: "linear-gradient(135deg, #D4A574, #F5DEB3)",
      },
      "Chai Tea": {
        icon: faSeedling,
        color: "#7C9D6E",
        gradient: "linear-gradient(135deg, #7C9D6E, #A8C4A0)",
      },
      Extra: {
        icon: faStar,
        color: "#C8956E",
        gradient: "linear-gradient(135deg, #C8956E, #E8C9A0)",
      },
    };
    return (
      styles[category] || {
        icon: faCoffee,
        color: "#6F4E37",
        gradient: "linear-gradient(135deg, #6F4E37, #8B6914)",
      }
    );
  };

  // Toggle section expansion
  const toggleSection = (category) => {
    setExpandedSections((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Featured items
  const featuredItems = useMemo(() => {
    return menuItems.filter((item) => item.availability && item.price > 15);
  }, [menuItems]);

  if (menuLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingContent}>
          <div style={styles.loadingSpinner}></div>
          <h2 style={styles.loadingTitle}>Brewing Menu...</h2>
          <p style={styles.loadingSubtitle}>
            Preparing something delicious for you
          </p>
        </div>
      </div>
    );
  }

  if (menuError) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorContent}>
          <FontAwesomeIcon icon={faCoffee} style={styles.errorIcon} />
          <h2 style={styles.errorTitle}>Oops! Something went wrong</h2>
          <p style={styles.errorSubtitle}>
            We're having trouble loading our menu. Please try again.
          </p>
          <button
            style={styles.retryButton}
            onClick={() => window.location.reload()}
          >
            Refresh Menu
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Background */}
      <div style={styles.backgroundWrapper}>
        <div style={styles.backgroundImage}></div>
        <div style={styles.backgroundOverlay}></div>
      </div>

      {/* Sticky Category Nav */}
      <div
        style={{
          ...styles.stickyNav,
          ...(isScrolled ? styles.stickyNavScrolled : {}),
        }}
      >
        <div style={styles.stickyNavContent}>
          <div style={styles.stickyNavCategories}>
            {categories.slice(0, 6).map((category) => {
              const style = getCategoryStyle(category);
              return (
                <button
                  key={category}
                  style={{
                    ...styles.stickyCategoryButton,
                    ...(selectedCategory === category
                      ? {
                          ...styles.stickyCategoryButtonActive,
                          borderColor: style.color,
                          color: style.color,
                        }
                      : {}),
                  }}
                  onClick={() => setSelectedCategory(category)}
                >
                  <FontAwesomeIcon
                    icon={style.icon}
                    style={styles.stickyCategoryIcon}
                  />
                  <span style={styles.stickyCategoryLabel}>{category}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div style={styles.heroSection}>
        <div style={styles.heroContent}>
          <div style={styles.heroBadge}>
            <FontAwesomeIcon icon={faHeart} style={styles.heroBadgeIcon} />
            <span>Est. 2024</span>
          </div>
          <h1 style={styles.heroTitle}>Cafe Name</h1>
          <p style={styles.heroSubtitle}>Where Every Cup Tells a Story</p>
          <div style={styles.heroInfo}>
            <div style={styles.heroInfoItem}>
              <FontAwesomeIcon icon={faClock} style={styles.heroInfoIcon} />
              <span>6:00 AM - 4:00 PM</span>
            </div>
            <div style={styles.heroInfoItem}>
              <FontAwesomeIcon
                icon={faLocationDot}
                style={styles.heroInfoIcon}
              />
              <span>123 Coffee Street</span>
            </div>
            <div style={styles.heroInfoItem}>
              <FontAwesomeIcon icon={faPhone} style={styles.heroInfoIcon} />
              <span>+233 123 456 789</span>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Items */}
      {featuredItems.length > 0 && (
        <div style={styles.featuredSection}>
          <div style={styles.sectionHeader}>
            <FontAwesomeIcon icon={faCrown} style={styles.sectionIcon} />
            <h2 style={styles.sectionTitle}>Featured</h2>
          </div>
          <div style={styles.featuredGrid}>
            {featuredItems.slice(0, 3).map((item) => (
              <div
                key={item.id}
                style={styles.featuredCard}
                onMouseEnter={() => setHoveredItem(item.id)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <div style={styles.featuredImageWrapper}>
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      style={styles.featuredImage}
                    />
                  ) : (
                    <div style={styles.featuredImagePlaceholder}>
                      <FontAwesomeIcon
                        icon={faCoffee}
                        style={styles.featuredPlaceholderIcon}
                      />
                    </div>
                  )}
                  <div style={styles.featuredBadge}>
                    <FontAwesomeIcon icon={faAward} />
                    <span>Bestseller</span>
                  </div>
                </div>
                <div style={styles.featuredContent}>
                  <h3 style={styles.featuredTitle}>{item.title}</h3>
                  <p style={styles.featuredDescription}>{item.description}</p>
                  <div style={styles.featuredPrice}>
                    GH₵{item.price.toFixed(2)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Menu Sections */}
      <div style={styles.menuSections}>
        {Object.entries(groupedItems).map(([category, items]) => {
          const style = getCategoryStyle(category);
          const isExpanded = expandedSections[category] !== false;

          return (
            <div key={category} style={styles.menuSection}>
              <div
                style={{ ...styles.categoryHeader, background: style.gradient }}
                onClick={() => toggleSection(category)}
                role="button"
                tabIndex={0}
              >
                <div style={styles.categoryHeaderContent}>
                  <div style={styles.categoryHeaderLeft}>
                    <FontAwesomeIcon
                      icon={style.icon}
                      style={styles.categoryHeaderIcon}
                    />
                    <h2 style={styles.categoryHeaderTitle}>{category}</h2>
                    <span style={styles.categoryHeaderCount}>
                      {items.length} items
                    </span>
                  </div>
                  <button style={styles.categoryToggle}>
                    <FontAwesomeIcon
                      icon={isExpanded ? faChevronUp : faChevronDown}
                    />
                  </button>
                </div>
              </div>

              {isExpanded && (
                <div style={styles.menuGrid}>
                  {items.map((item) => (
                    <div
                      key={item.id}
                      style={styles.menuCard}
                      onMouseEnter={() => setHoveredItem(item.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      <div style={styles.menuCardContent}>
                        <div style={styles.menuCardInfo}>
                          <div style={styles.menuCardHeader}>
                            <h3 style={styles.menuCardTitle}>{item.title}</h3>
                            {!item.availability && (
                              <span style={styles.unavailableBadge}>
                                Unavailable
                              </span>
                            )}
                          </div>
                          {item.description && (
                            <p style={styles.menuCardDescription}>
                              {item.description}
                            </p>
                          )}
                          <div style={styles.menuCardFooter}>
                            <span style={styles.menuCardPrice}>
                              GH₵{item.price.toFixed(2)}
                            </span>
                            {item.availability && (
                              <div style={styles.availabilityIndicator}>
                                <FontAwesomeIcon
                                  icon={faCircle}
                                  style={styles.availabilityDot}
                                />
                                <span style={styles.availabilityText}>
                                  Available
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        {item.imageUrl && (
                          <div style={styles.menuCardImageWrapper}>
                            <img
                              src={item.imageUrl}
                              alt={item.title}
                              style={styles.menuCardImage}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Extra Section */}
      <div style={styles.extraSection}>
        <div style={styles.extraHeader}>
          <FontAwesomeIcon icon={faStar} style={styles.extraIcon} />
          <h2 style={styles.extraTitle}>Extra</h2>
        </div>
        <div style={styles.extraGrid}>
          <div style={styles.extraCard}>
            <div style={styles.extraEmoji}>🧋</div>
            <h3 style={styles.extraName}>BOBA</h3>
            <p style={styles.extraDescription}>Chewy tapioca pearls</p>
          </div>
          <div style={styles.extraCard}>
            <div style={styles.extraEmoji}>☕</div>
            <h3 style={styles.extraName}>COFFEE</h3>
            <p style={styles.extraDescription}>Extra espresso shot</p>
          </div>
          <div style={styles.extraCard}>
            <div style={styles.extraEmoji}>🍯</div>
            <h3 style={styles.extraName}>SPECIAL JELLY SYRUP</h3>
            <p style={styles.extraDescription}>House-made jelly syrup</p>
          </div>
        </div>
      </div>

      {/* Fresh Brewed Section */}
      <div style={styles.freshBrewedSection}>
        <div style={styles.freshBrewedContent}>
          <FontAwesomeIcon icon={faMugHot} style={styles.freshBrewedIcon} />
          <h2 style={styles.freshBrewedTitle}>Fresh Brewed Coffee</h2>
          <p style={styles.freshBrewedSubtitle}>Everyday • Made with Love</p>
          <div style={styles.freshBrewedDivider}></div>
          <p style={styles.freshBrewedDescription}>
            Experience the perfect cup of coffee, freshly brewed with carefully
            selected beans from the finest plantations around the world.
          </p>
        </div>
      </div>

      {/* Contact & Footer */}
      <div style={styles.contactSection}>
        <div style={styles.contactContent}>
          <div style={styles.contactInfo}>
            <div style={styles.contactItem}>
              <FontAwesomeIcon icon={faPhone} style={styles.contactIcon} />
              <div>
                <h4 style={styles.contactLabel}>Phone</h4>
                <p style={styles.contactValue}>+233 123 456 789</p>
              </div>
            </div>
            <div style={styles.contactItem}>
              <FontAwesomeIcon icon={faEnvelope} style={styles.contactIcon} />
              <div>
                <h4 style={styles.contactLabel}>Email</h4>
                <p style={styles.contactValue}>info@cafename.com</p>
              </div>
            </div>
            <div style={styles.contactItem}>
              <FontAwesomeIcon
                icon={faLocationDot}
                style={styles.contactIcon}
              />
              <div>
                <h4 style={styles.contactLabel}>Location</h4>
                <p style={styles.contactValue}>123 Coffee Street, City</p>
              </div>
            </div>
          </div>
          <div style={styles.footerDivider}></div>
          <div style={styles.footerBottom}>
            <p style={styles.footerText}>
              © 2024 Cafe Name. All rights reserved.
            </p>
            <p style={styles.footerText}>
              Made with{" "}
              <FontAwesomeIcon icon={faHeart} style={styles.footerHeart} /> in
              Ghana
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: "1400px",
    margin: "0 auto",
    padding: "20px 20px 40px",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    minHeight: "100vh",
    position: "relative",
    backgroundColor: "#faf8f5",
    WebkitFontSmoothing: "antialiased",
  },
  backgroundWrapper: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    overflow: "hidden",
  },
  backgroundImage: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url('/src/images/defaultimage.jpg')`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
  },
  backgroundOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(250, 248, 245, 0.92)",
  },
  stickyNav: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    marginBottom: "30px",
    transition: "all 0.3s ease",
    borderRadius: "16px",
    overflow: "hidden",
  },
  stickyNavScrolled: {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    backdropFilter: "blur(20px)",
    padding: "8px 0",
  },
  stickyNavContent: {
    display: "flex",
    justifyContent: "center",
    padding: "12px 20px",
  },
  stickyNavCategories: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    justifyContent: "center",
  },
  stickyCategoryButton: {
    padding: "8px 16px",
    backgroundColor: "transparent",
    border: "2px solid transparent",
    borderRadius: "25px",
    fontSize: "13px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    color: "#4A3728",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    whiteSpace: "nowrap",
  },
  stickyCategoryButtonActive: {
    backgroundColor: "white",
    borderColor: "#6F4E37",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  stickyCategoryIcon: {
    fontSize: "14px",
  },
  stickyCategoryLabel: {
    fontSize: "12px",
  },
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f8f4f0, #f5ede6)",
  },
  loadingContent: {
    textAlign: "center",
  },
  loadingSpinner: {
    width: "60px",
    height: "60px",
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #6F4E37",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    margin: "0 auto 20px",
  },
  loadingTitle: {
    fontSize: "24px",
    color: "#2C1810",
    marginBottom: "8px",
    fontWeight: "600",
  },
  loadingSubtitle: {
    fontSize: "16px",
    color: "#6B5B4F",
  },
  errorContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f8f4f0, #f5ede6)",
  },
  errorContent: {
    textAlign: "center",
    padding: "40px",
  },
  errorIcon: {
    fontSize: "48px",
    color: "#6F4E37",
    marginBottom: "20px",
  },
  errorTitle: {
    fontSize: "28px",
    color: "#2C1810",
    marginBottom: "10px",
    fontWeight: "600",
  },
  errorSubtitle: {
    fontSize: "16px",
    color: "#6B5B4F",
    marginBottom: "30px",
  },
  retryButton: {
    padding: "12px 32px",
    backgroundColor: "#6F4E37",
    color: "white",
    border: "none",
    borderRadius: "30px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  heroSection: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
    padding: "80px 30px 60px",
    marginBottom: "50px",
    background:
      "linear-gradient(135deg, rgba(44, 24, 16, 0.97), rgba(44, 24, 16, 0.92))",
    borderRadius: "28px",
    boxShadow: "0 30px 80px rgba(0,0,0,0.2)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255,255,255,0.05)",
  },
  heroContent: {
    maxWidth: "800px",
    margin: "0 auto",
  },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "rgba(255,255,255,0.08)",
    padding: "8px 20px",
    borderRadius: "30px",
    fontSize: "13px",
    color: "#D4C5B0",
    marginBottom: "25px",
    border: "1px solid rgba(255,255,255,0.05)",
  },
  heroBadgeIcon: {
    color: "#C8956E",
    fontSize: "12px",
  },
  heroTitle: {
    fontSize: "72px",
    fontWeight: "800",
    letterSpacing: "16px",
    color: "#F5E6D3",
    margin: "0 0 12px 0",
    textShadow: "0 4px 20px rgba(0,0,0,0.2)",
    lineHeight: "1.1",
  },
  heroSubtitle: {
    fontSize: "22px",
    color: "#D4C5B0",
    letterSpacing: "6px",
    margin: "0 0 30px 0",
    fontStyle: "italic",
    fontWeight: "300",
  },
  heroInfo: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "35px",
  },
  heroInfoItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    color: "#D4C5B0",
    fontSize: "15px",
    fontWeight: "400",
  },
  heroInfoIcon: {
    color: "#C8956E",
    fontSize: "18px",
  },
  featuredSection: {
    position: "relative",
    zIndex: 1,
    marginBottom: "50px",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    marginBottom: "25px",
  },
  sectionIcon: {
    color: "#C8956E",
    fontSize: "28px",
  },
  sectionTitle: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#2C1810",
    margin: 0,
    letterSpacing: "-0.5px",
  },
  featuredGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "25px",
  },
  featuredCard: {
    backgroundColor: "white",
    borderRadius: "20px",
    overflow: "hidden",
    boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
    transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    cursor: "pointer",
  },
  featuredImageWrapper: {
    position: "relative",
    height: "220px",
    overflow: "hidden",
    backgroundColor: "#f5f0eb",
  },
  featuredImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform 0.6s ease",
  },
  featuredImagePlaceholder: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f5f0eb",
  },
  featuredPlaceholderIcon: {
    fontSize: "48px",
    color: "#C8956E",
    opacity: 0.5,
  },
  featuredBadge: {
    position: "absolute",
    top: "16px",
    right: "16px",
    backgroundColor: "#FF6B35",
    color: "white",
    padding: "6px 16px",
    borderRadius: "25px",
    fontSize: "12px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    boxShadow: "0 4px 12px rgba(255, 107, 53, 0.3)",
  },
  featuredContent: {
    padding: "24px",
  },
  featuredTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#2C1810",
    margin: "0 0 8px 0",
  },
  featuredDescription: {
    fontSize: "14px",
    color: "#6B5B4F",
    margin: "0 0 12px 0",
    lineHeight: "1.6",
  },
  featuredPrice: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#6F4E37",
  },
  menuSections: {
    position: "relative",
    zIndex: 1,
  },
  menuSection: {
    marginBottom: "30px",
  },
  categoryHeader: {
    padding: "18px 24px",
    borderRadius: "16px 16px 0 0",
    color: "white",
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  categoryHeaderContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  categoryHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  categoryHeaderIcon: {
    fontSize: "24px",
  },
  categoryHeaderTitle: {
    fontSize: "20px",
    fontWeight: "600",
    margin: 0,
    letterSpacing: "1px",
  },
  categoryHeaderCount: {
    fontSize: "13px",
    opacity: 0.9,
    backgroundColor: "rgba(255,255,255,0.15)",
    padding: "2px 12px",
    borderRadius: "20px",
    fontWeight: "400",
  },
  categoryToggle: {
    background: "rgba(255,255,255,0.15)",
    border: "none",
    color: "white",
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s ease",
  },
  menuGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: "16px",
    backgroundColor: "rgba(255,255,255,0.95)",
    padding: "20px",
    borderRadius: "0 0 16px 16px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
  },
  menuCard: {
    backgroundColor: "white",
    borderRadius: "14px",
    padding: "16px",
    border: "1px solid #f0ebe6",
    transition: "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
  },
  menuCardContent: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-start",
  },
  menuCardInfo: {
    flex: 1,
    minWidth: 0,
  },
  menuCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "8px",
    marginBottom: "4px",
  },
  menuCardTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#2C1810",
    margin: 0,
    lineHeight: "1.3",
  },
  menuCardDescription: {
    fontSize: "13px",
    color: "#6B5B4F",
    margin: "4px 0 8px 0",
    lineHeight: "1.5",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  menuCardFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "8px",
    flexWrap: "wrap",
    gap: "8px",
  },
  menuCardPrice: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#6F4E37",
  },
  availabilityIndicator: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    color: "#10B981",
    fontWeight: "500",
  },
  availabilityDot: {
    fontSize: "8px",
    color: "#10B981",
  },
  availabilityText: {
    fontSize: "12px",
  },
  menuCardImageWrapper: {
    width: "72px",
    height: "72px",
    flexShrink: 0,
    borderRadius: "12px",
    overflow: "hidden",
  },
  menuCardImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  unavailableBadge: {
    backgroundColor: "#FEE2E2",
    color: "#EF4444",
    fontSize: "11px",
    fontWeight: "600",
    padding: "2px 10px",
    borderRadius: "12px",
    whiteSpace: "nowrap",
  },
  extraSection: {
    position: "relative",
    zIndex: 1,
    backgroundColor: "rgba(255,255,255,0.95)",
    borderRadius: "20px",
    padding: "32px",
    marginBottom: "30px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
    backdropFilter: "blur(10px)",
  },
  extraHeader: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    marginBottom: "28px",
  },
  extraIcon: {
    fontSize: "32px",
    color: "#C8956E",
  },
  extraTitle: {
    fontSize: "26px",
    fontWeight: "700",
    color: "#2C1810",
    margin: 0,
  },
  extraGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "20px",
  },
  extraCard: {
    textAlign: "center",
    padding: "24px 16px",
    backgroundColor: "#faf8f5",
    borderRadius: "16px",
    transition: "all 0.3s ease",
    border: "1px solid transparent",
  },
  extraEmoji: {
    fontSize: "44px",
    marginBottom: "12px",
    display: "block",
  },
  extraName: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#2C1810",
    margin: "0 0 4px 0",
  },
  extraDescription: {
    fontSize: "13px",
    color: "#6B5B4F",
    margin: 0,
  },
  freshBrewedSection: {
    position: "relative",
    zIndex: 1,
    background:
      "linear-gradient(135deg, rgba(44, 24, 16, 0.97), rgba(44, 24, 16, 0.93))",
    borderRadius: "20px",
    padding: "70px 40px",
    textAlign: "center",
    marginBottom: "30px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255,255,255,0.05)",
  },
  freshBrewedContent: {
    maxWidth: "650px",
    margin: "0 auto",
  },
  freshBrewedIcon: {
    fontSize: "60px",
    color: "#C8956E",
    marginBottom: "16px",
  },
  freshBrewedTitle: {
    fontSize: "40px",
    fontWeight: "700",
    letterSpacing: "6px",
    color: "#F5E6D3",
    margin: "0 0 8px 0",
  },
  freshBrewedSubtitle: {
    fontSize: "20px",
    color: "#D4C5B0",
    letterSpacing: "8px",
    margin: "0 0 24px 0",
    fontWeight: "300",
  },
  freshBrewedDivider: {
    width: "80px",
    height: "2px",
    backgroundColor: "#C8956E",
    margin: "0 auto 24px",
  },
  freshBrewedDescription: {
    fontSize: "16px",
    color: "#D4C5B0",
    lineHeight: "1.9",
    margin: 0,
    fontWeight: "300",
  },
  contactSection: {
    position: "relative",
    zIndex: 1,
    backgroundColor: "rgba(255,255,255,0.95)",
    borderRadius: "20px",
    padding: "40px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
    backdropFilter: "blur(10px)",
  },
  contactContent: {
    maxWidth: "900px",
    margin: "0 auto",
  },
  contactInfo: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "24px",
    marginBottom: "30px",
  },
  contactItem: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "16px 20px",
    backgroundColor: "#faf8f5",
    borderRadius: "14px",
    transition: "all 0.3s ease",
    border: "1px solid transparent",
  },
  contactIcon: {
    fontSize: "26px",
    color: "#C8956E",
    flexShrink: 0,
  },
  contactLabel: {
    fontSize: "11px",
    fontWeight: "600",
    color: "#6B5B4F",
    margin: "0 0 2px 0",
    textTransform: "uppercase",
    letterSpacing: "1.5px",
  },
  contactValue: {
    fontSize: "15px",
    color: "#2C1810",
    margin: 0,
    fontWeight: "500",
  },
  footerDivider: {
    height: "1px",
    backgroundColor: "#e8ddd3",
    marginBottom: "24px",
  },
  footerBottom: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
  },
  footerText: {
    fontSize: "13px",
    color: "#6B5B4F",
    margin: 0,
  },
  footerHeart: {
    color: "#FF6B35",
    fontSize: "13px",
  },
};

// Global styles to add to your CSS
const globalStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 1024px) {
    .hero-title {
      font-size: 56px !important;
      letter-spacing: 10px !important;
    }
    .menu-grid {
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)) !important;
    }
  }

  @media (max-width: 768px) {
    .container {
      padding: 12px !important;
    }
    .hero-title {
      font-size: 40px !important;
      letter-spacing: 6px !important;
    }
    .hero-subtitle {
      font-size: 18px !important;
      letter-spacing: 3px !important;
    }
    .hero-info {
      gap: 20px !important;
      flex-direction: column !important;
    }
    .hero-section {
      padding: 50px 20px 40px !important;
    }
    .menu-grid {
      grid-template-columns: 1fr !important;
      padding: 12px !important;
      gap: 12px !important;
    }
    .featured-grid {
      grid-template-columns: 1fr !important;
    }
    .contact-info {
      grid-template-columns: 1fr !important;
    }
    .category-header {
      padding: 14px 18px !important;
    }
    .category-header-title {
      font-size: 17px !important;
    }
    .category-header-count {
      font-size: 11px !important;
    }
    .extra-grid {
      grid-template-columns: 1fr !important;
    }
    .sticky-category-label {
      display: none !important;
    }
    .sticky-category-button {
      padding: 8px 12px !important;
    }
    .fresh-brewed-title {
      font-size: 28px !important;
      letter-spacing: 4px !important;
    }
    .fresh-brewed-subtitle {
      font-size: 16px !important;
      letter-spacing: 4px !important;
    }
    .fresh-brewed-section {
      padding: 40px 20px !important;
    }
    .contact-section {
      padding: 24px !important;
    }
    .footer-bottom {
      flex-direction: column !important;
      text-align: center !important;
    }
    .extra-section {
      padding: 20px !important;
    }
    .featured-card {
      margin: 0 !important;
    }
  }

  @media (max-width: 480px) {
    .hero-title {
      font-size: 32px !important;
      letter-spacing: 4px !important;
    }
    .hero-subtitle {
      font-size: 15px !important;
      letter-spacing: 2px !important;
    }
    .hero-info-item {
      font-size: 13px !important;
    }
    .section-title {
      font-size: 22px !important;
    }
    .extra-title {
      font-size: 22px !important;
    }
    .menu-card {
      padding: 14px !important;
    }
    .menu-card-title {
      font-size: 15px !important;
    }
    .menu-card-price {
      font-size: 16px !important;
    }
    .featured-title {
      font-size: 18px !important;
    }
    .category-button {
      font-size: 11px !important;
      padding: 6px 12px !important;
    }
  }

  /* Scrollbar styling */
  ::-webkit-scrollbar {
    width: 8px;
  }
  ::-webkit-scrollbar-track {
    background: #f5ede6;
  }
  ::-webkit-scrollbar-thumb {
    background: #C8956E;
    border-radius: 10px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: #B0845F;
  }
`;
